import { useMemo, useState } from "react";
import { categories, feedItems, stats, hotList } from "@/mocks/feed";

interface HeroProps {
  onSearch: (q: string) => void;
  onCategory: (id: string) => void;
}

export default function Hero({ onSearch, onCategory }: HeroProps) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const suggestions = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return feedItems.slice(0, 4);
    return feedItems.filter((f) => f.title.toLowerCase().includes(query) || f.tags.join("").toLowerCase().includes(query)).slice(0, 4);
  }, [q]);

  const submit = () => {
    setOpen(false);
    if (q.trim()) {
      onSearch(q.trim());
    } else {
      document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="breaking" className="relative w-full overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary-700 via-primary-600 to-background-900"></div>
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20warm%20orange%20and%20amber%20textured%20background%20with%20soft%20glowing%20light%20streaks%20and%20bokeh%20orbs%20like%20popcorn%20butter%20and%20evening%20stage%20lights%2C%20smooth%20cinematic%20gradient%2C%20stylish%20artistic%20backdrop%2C%20no%20text&width=1600&height=900&seq=gua-hero&orientation=landscape"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/40"></div>

      <div className="relative mx-auto grid w-full max-w-[1200px] gap-8 px-4 py-12 md:px-6 lg:grid-cols-12 lg:py-16">
        <div className="flex flex-col items-start lg:col-span-7">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-live rounded-full bg-accent-400"></span>
            </span>
            前线在线 · 热点持续回刷中
          </span>

          <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 md:text-5xl md:leading-[1.1]">
            蹲守第一现场
            <br />
            一手吃瓜 <span className="text-accent-300">不过夜</span>
          </h1>

          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
            吃瓜前线站在事件最前沿：一手实拍、目击细节、原发经过，不靠二手转述。综艺现场、直播名场面、圈内动态与生活奇闻，想看完整事件链就进首页，随时回刷都有新爆点。
          </p>

          {/* search */}
          <div className="relative mt-6 w-full max-w-xl">
            <div className="flex items-center gap-2 rounded-lg bg-background-50 px-4 py-2.5">
              <i className="ri-search-line text-xl text-foreground-500" aria-hidden="true"></i>
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="输入事件名或热梗，直达前线"
                className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
                aria-label="搜索前线内容"
              />
              {q ? (
                <button type="button" onClick={() => setQ("")} aria-label="清空" className="text-foreground-400 hover:text-foreground-700 cursor-pointer">
                  <i className="ri-close-circle-fill text-lg" aria-hidden="true"></i>
                </button>
              ) : null}
              <button
                type="button"
                onClick={submit}
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                前线直击
              </button>
            </div>

            {open ? (
              <div className="absolute inset-x-0 top-full z-20 mt-2 overflow-hidden rounded-lg bg-background-50">
                {suggestions.length ? (
                  <ul className="py-1">
                    {suggestions.map((s) => (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setQ(s.title);
                            submit();
                          }}
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-foreground-800 hover:bg-secondary-100 cursor-pointer"
                        >
                          <i className="ri-flashlight-fill text-primary-500" aria-hidden="true"></i>
                          <span className="line-clamp-1">{s.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-4 py-3 text-sm text-foreground-600">前线暂无匹配，换个词再试试。</p>
                )}
              </div>
            ) : null}
          </div>

          {/* category chips */}
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-background-100/80">热点分类</span>
            {categories
              .filter((c) => c.id !== "all")
              .map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onCategory(c.id);
                    document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="rounded-full border border-background-50/30 bg-background-50/10 px-3 py-1.5 text-xs font-medium text-background-50 transition-colors hover:border-accent-400 hover:bg-background-50/20 whitespace-nowrap cursor-pointer"
                >
                  {c.name}
                </button>
              ))}
          </div>

          {/* stats */}
          <dl className="mt-8 grid w-full max-w-xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-background-50/20 bg-background-50/10 backdrop-blur-sm sm:grid-cols-4">
            {stats.map((s) => (
              <div key={s.value} className="flex flex-col items-center gap-0.5 px-3 py-4">
                <dt className="order-2 text-xs text-background-100/80">{s.value}</dt>
                <dd className="order-1 font-heading text-2xl font-extrabold text-accent-300">
                  {s.num}
                  <span className="ml-0.5 text-sm font-medium text-background-100/80">{s.unit}</span>
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* radar panel */}
        <aside className="lg:col-span-5 lg:flex lg:items-center" aria-label="前线雷达">
          <div className="w-full rounded-2xl border border-background-50/20 bg-background-50/10 p-5 backdrop-blur-md">
            <div className="flex items-center justify-between border-b border-background-50/15 pb-3">
              <h2 className="flex items-center gap-2 text-sm font-bold text-background-50">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-live rounded-full bg-accent-400"></span>
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent-400"></span>
                </span>
                前线雷达
              </h2>
              <a
                href="#hot"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("hot")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-xs text-accent-300 hover:text-accent-200 whitespace-nowrap cursor-pointer"
              >
                全量榜单 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </a>
            </div>

            <ul className="mt-3 space-y-3">
              {hotList.slice(0, 3).map((h) => (
                <li key={h.rank}>
                  <a
                    href="#feed"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById("feed")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="group flex items-start gap-3 rounded-xl border border-transparent p-2 transition-colors hover:border-background-50/20 hover:bg-background-50/10 cursor-pointer"
                  >
                    <span className="font-heading text-xl font-extrabold text-background-50/40 transition-colors group-hover:text-accent-400">
                      {String(h.rank).padStart(2, "0")}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-background-50">{h.title}</span>
                      <span className="mt-0.5 flex items-center gap-2 text-xs text-background-100/70">
                        {h.live ? (
                          <span className="flex items-center gap-1 text-accent-300">
                            <i className="ri-record-circle-fill animate-live text-[10px]" aria-hidden="true"></i> 实况
                          </span>
                        ) : (
                          <span>{h.category}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line" aria-hidden="true"></i> 热度 {h.heat}
                        </span>
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>

            <a
              href="#chain"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("chain")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="mt-3 flex items-center gap-2 rounded-lg bg-background-50/15 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/25 cursor-pointer"
            >
              <i className="ri-timeline-view" aria-hidden="true"></i>
              本期完整事件链已归档
              <i className="ml-auto ri-arrow-right-line" aria-hidden="true"></i>
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}